import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icons } from 'sarsaparilla';
import { isDescendant } from '../utilities/tools';
import { getLocationDisplayName } from '../utilities/locations';
import LaunchLocationSelect from './LaunchLocationSelect';

const propTypes = {
  id: PropTypes.string,
  filterID: PropTypes.string,
  selectedLocation: PropTypes.object,
  locationSelect: PropTypes.func.isRequired,
  handleSelectorClick: PropTypes.func,
  initiallySelectedLocation: PropTypes.object,
  showLocationTypePicker: PropTypes.bool,
  placeholder: PropTypes.string,
  launchButtonAppearance: PropTypes.string,
  launchButtonIconIsChevron: PropTypes.bool,
  launchButtonRoleText: PropTypes.string,
  launchButtonClassName: PropTypes.string,
  clearFuncHack: PropTypes.func,
  showReservableLocations: PropTypes.bool,
  allowAllLocationSelect: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
  displaySelectedLocation: PropTypes.bool,
  selectableLocationTypes: PropTypes.arrayOf(PropTypes.string),
  visibleLocationTypes: PropTypes.arrayOf(PropTypes.string),
  disableConcessionaireHierarchy: PropTypes.bool,
  allowHierarchyFillSelection: PropTypes.bool,
};

const defaultProps = {
  filterID: 'location-picker',
  launchButtonAppearance: 'tertiary',
  launchButtonIconIsChevron: false,
  launchButtonClassName: '',
  showReservableLocations: false,
  allowAllLocationSelect: false,
  closeOnSelect: true,
  displaySelectedLocation: true,
  disableConcessionaireHierarchy: false,
  allowHierarchyFillSelection: false,
};

export default function LocationPicker(props) {
  const [locationFocused, setLocationFocused] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  //What follows is a hacky way to have two props that set the same state.
  //This was a pre-existing condition that I am replicating.
  //Luckily useEffects seem to be called in order.

  // Called every time selectedLocation prop changes.
  useEffect(() => {
    setSelectedLocation(props.selectedLocation);
  }, [props.selectedLocation]);

  // Called once.
  useEffect(() => {
    if (props.initiallySelectedLocation) {
      setSelectedLocation(props.initiallySelectedLocation);
    }
  }, []);

  //end hack

  function clearLocationSelection() {
    setLocationFocused(false);
    setSelectedLocation(null);
  }

  useEffect(() => {
    if (props.clearFuncHack) {
      props.clearFuncHack(clearLocationSelection);
    }
  }, [props.clearFuncHack]);

  function globalCollapse(evt) {
    let selectorChild = false;
    const stopAts = [...document.getElementsByClassName('launch-location-select-grid')];
    stopAts.forEach((parent) => {
      selectorChild = selectorChild || isDescendant(parent, evt.target);
    });

    if (!selectorChild) {
      if (!props.closeOnSelect) {
        setLocationFocused(false);
      }
    } else {
      document.getElementsByTagName('body')[0].addEventListener('mousedown', globalCollapse, { once: true });
    }
  }

  useEffect(() => {
    if (locationFocused) {
      document.getElementsByTagName('body')[0].addEventListener('mousedown', globalCollapse, { once: true });
    }
  }, [locationFocused]);

  function handleSelectorClick(e) {
    e.stopPropagation();

    const event = document.createEvent('Event');
    event.initEvent('click', true, true);
    document.dispatchEvent(event);

    const setTo = !locationFocused;
    setLocationFocused(setTo);

    if (props.handleSelectorClick) {
      props.handleSelectorClick();
    }
  }

  function handleLocationClick(row, facility) {
    if (props.closeOnSelect) {
      setLocationFocused(false);
    }
    if (props.displaySelectedLocation) {
      setSelectedLocation(row);
    }
    props.locationSelect(row, facility);
  }

  function launchButtonIcon() {
    const { launchButtonIconIsChevron } = props;
    if (launchButtonIconIsChevron) {
      return locationFocused ? <Icons.IconChevronUp size="md" /> : <Icons.IconChevronDown size="md" />;
    }
    return locationFocused ? <Icons.IconArrowUp size="md" /> : <Icons.IconArrowDown size="md" />;
  }

  let locationButtonTitle = props.placeholder || 'Select Location';
  if (selectedLocation) {
    locationButtonTitle = getLocationDisplayName(selectedLocation);
    if (props.launchButtonRoleText) {
      locationButtonTitle = `${locationButtonTitle}, ${props.launchButtonRoleText}`;
    }
  }
  return (
    <div className="location-picker">
      <div className="ia-location-selector">
        <Button
          appearance={props.launchButtonAppearance}
          shouldFitContainer
          className={props.launchButtonClassName}
          iconAfterElement={launchButtonIcon()}
          onClick={handleSelectorClick}
        >
          {locationButtonTitle}
        </Button>
        <LaunchLocationSelect
          filterID={props.filterID}
          collapsed={!locationFocused}
          locationSelect={handleLocationClick}
          allowAllLocationSelect={props.allowAllLocationSelect}
          showReservableLocations={props.showReservableLocations}
          selectableLocationTypes={props.selectableLocationTypes}
          disableConcessionaireHierarchy={props.disableConcessionaireHierarchy}
          allowHierarchyFillSelection={props.allowHierarchyFillSelection}
          visibleLocationTypes={props.visibleLocationTypes}
        />
      </div>
    </div>
  );
}

LocationPicker.propTypes = propTypes;
LocationPicker.defaultProps = defaultProps;
